import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../api/api';
import Layout from '../components/Layout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShieldAlt, faCheck, faSave, faUsers, faUser,
    faSearch, faTimes, faSyncAlt, faBuilding
} from '@fortawesome/free-solid-svg-icons';
import { useAuth, ROLES } from '../context/AuthContext';

const ROLE_INFO = {
    99: { label: 'Superadmin', icon: faShieldAlt, desc: 'Platform owner — global CRUD across all resources and organizations' },
    98: { label: 'Org Admin', icon: faShieldAlt, desc: 'Organization Administrator — manage users, roles, and records in own organization' },
    97: { label: 'Manager', icon: faUsers, desc: 'Team Manager — CRUD on own and direct reports\' accounts, opportunities, targets' },
    96: { label: 'Employee', icon: faUser, desc: 'Sales Rep — Create, Read, Update on own records only' },
};

function AccessControl() {
    const { isSuper, isAdmin, currentUser, activeOrgId } = useAuth();
    const [permissions, setPermissions] = useState([]);
    const [users, setUsers] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState(() => {
        if (!isSuper) return currentUser?.OrgID || 1;
        return activeOrgId && activeOrgId !== 'all' ? Number(activeOrgId) : 1;
    });
    const [loading, setLoading] = useState(true);
    const [saveMessage, setSaveMessage] = useState('');
    const [userSearch, setUserSearch] = useState('');
    const [selectedRoleFilter, setSelectedRoleFilter] = useState('ALL');
    const [activeMatrixTab, setActiveMatrixTab] = useState('ALL');
    const [entries, setEntries] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const selectedOrg = useMemo(() => {
        return organizations.find(o => Number(o.OrgID) === Number(selectedOrgId));
    }, [organizations, selectedOrgId]);

    const fetchRoleData = useCallback(async (orgId) => {
        const targetId = Number(orgId) || 1;
        try {
            setLoading(true);
            const res = await api.get(`/roles?orgId=${targetId}`, {
                headers: { 'X-Org-Id': String(targetId) }
            });
            setPermissions(res.data.permissions || []);
            setUsers(res.data.users || []);
        } catch (err) {
            console.error("Failed to load role permissions:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchOrgs = useCallback(async () => {
        if (!isSuper) return;
        try {
            const res = await api.get('/organizations');
            setOrganizations(res.data || []);
        } catch (err) {
            console.error("Failed to fetch organizations:", err);
        }
    }, [isSuper]);

    // Keep selectedOrgId aligned if activeOrgId changes from topbar switcher
    useEffect(() => {
        if (isSuper && activeOrgId && activeOrgId !== 'all') {
            setSelectedOrgId(Number(activeOrgId));
        }
    }, [isSuper, activeOrgId]);

    // Ensure selectedOrgId defaults to a valid organization if current value not in loaded list
    useEffect(() => {
        if (isSuper && organizations.length > 0) {
            const exists = organizations.some(o => Number(o.OrgID) === Number(selectedOrgId));
            if (!exists) {
                setSelectedOrgId(Number(organizations[0].OrgID));
            }
        }
    }, [isSuper, organizations, selectedOrgId]);

    useEffect(() => {
        fetchRoleData(selectedOrgId);
        fetchOrgs();
    }, [fetchRoleData, fetchOrgs, selectedOrgId]);

    const handleOrgChange = (newOrgId) => {
        const id = Number(newOrgId);
        setSelectedOrgId(id);
        setCurrentPage(1);
    };

    const handleTogglePermission = (permissionId, field) => {
        setPermissions(prev => prev.map(p => {
            if (p.PermissionID === permissionId) {
                return { ...p, [field]: !p[field] };
            }
            return p;
        }));
    };

    const handleSavePermission = async (perm) => {
        try {
            await api.put(`/roles/${perm.RoleID}?orgId=${selectedOrgId}`, {
                ...perm,
                OrgID: selectedOrgId
            }, {
                headers: { 'X-Org-Id': String(selectedOrgId) }
            });
            const orgLabel = selectedOrg?.OrgName || `Org #${selectedOrgId}`;
            setSaveMessage(`Permissions saved for ${ROLE_INFO[perm.RoleID]?.label || `Role ${perm.RoleID}`} (${perm.Module}) in ${orgLabel}!`);
            setTimeout(() => setSaveMessage(''), 4000);
        } catch (err) {
            console.error("Failed to save permission:", err);
            alert(err.response?.data?.error || "Failed to save role permission rule");
        }
    };

    const handleUserRoleChange = async (empId, newRoleId) => {
        try {
            await api.put(`/roles/users/${empId}`, { RoleID: Number(newRoleId) });
            setUsers(prev => prev.map(u => u.empId === empId ? { ...u, RoleID: Number(newRoleId) } : u));
            setSaveMessage(`User #${empId} role updated to ${ROLE_INFO[newRoleId]?.label || newRoleId}!`);
            setTimeout(() => setSaveMessage(''), 4000);
        } catch (err) {
            console.error("Failed to update user role:", err);
            alert(err.response?.data?.error || "Failed to update user role");
        }
    };

    // Filter users by search term and selected role filter
    const filteredUsers = useMemo(() => {
        return users.filter(u => {
            const matchesRole = selectedRoleFilter === 'ALL' || String(u.RoleID) === String(selectedRoleFilter);
            if (!matchesRole) return false;

            if (!userSearch.trim()) return true;
            const term = userSearch.toLowerCase();
            return (
                u.name?.toLowerCase().includes(term) ||
                u.email?.toLowerCase().includes(term) ||
                u.designation?.toLowerCase().includes(term) ||
                String(u.empId).includes(term)
            );
        });
    }, [users, userSearch, selectedRoleFilter]);

    // Paginated users
    const totalPages = Math.ceil(filteredUsers.length / entries);
    const paginatedUsers = useMemo(() => {
        const start = (currentPage - 1) * entries;
        return filteredUsers.slice(start, start + entries);
    }, [filteredUsers, currentPage, entries]);

    if (!isAdmin) {
        return (
            <Layout>
                <div className="dome-dashboard-wrap">
                    <div className="dome-dash-card p-40 text-center">
                        <div className="text-32 text-danger mb-12">
                            <FontAwesomeIcon icon={faShieldAlt} />
                        </div>
                        <h2 className="font-18 text-dark font-600 mb-6">Administrator Access Required</h2>
                        <p className="text-muted font-13 mb-16">
                            Only administrators have permission to view and modify role access rules.
                        </p>
                    </div>
                </div>
            </Layout>
        );
    }

    const superadminPermissions = permissions.filter(p => p.RoleID === 99);
    const orgAdminPermissions = permissions.filter(p => p.RoleID === 98);
    const managerPermissions = permissions.filter(p => p.RoleID === 97);
    const employeePermissions = permissions.filter(p => p.RoleID === 96);

    const matrixSections = [
        ...(isSuper ? [{ roleId: 99, title: 'Superadmin (Role 99)', perms: superadminPermissions, editable: true }] : []),
        { roleId: 98, title: 'Org Admin (Role 98)', perms: orgAdminPermissions, editable: isSuper },
        { roleId: 97, title: 'Manager (Role 97)', perms: managerPermissions, editable: true },
        { roleId: 96, title: 'Employee (Role 96)', perms: employeePermissions, editable: true }
    ].filter(s => activeMatrixTab === 'ALL' || s.roleId === activeMatrixTab);

    return (
        <Layout>
            <div className="dome-dashboard-wrap">

                {/* ================= 1. TOP HEADER ================= */}
                <div className="dome-dash-header-row">
                    <div className="dome-dash-title-wrap">
                        <h1 className="dome-dash-h1">Role Access &amp; Permissions</h1>
                    </div>

                    <div className="dome-dash-actions">
                        {isSuper && organizations.length > 0 && (
                            <div className="dome-mini-filter-item">
                                <label className="dome-filter-mini-label">Policy Organization:</label>
                                <select
                                    value={selectedOrgId}
                                    onChange={(e) => handleOrgChange(e.target.value)}
                                    className="dome-stacked-select"
                                >
                                    {organizations.map(o => (
                                        <option key={o.OrgID} value={o.OrgID}>
                                            {o.OrgName} ({o.OrgCode})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <button
                            type="button"
                            className="btn-dome-secondary"
                            onClick={() => fetchRoleData(selectedOrgId)}
                            title="Refresh Permissions"
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                {/* Save Toast Notification */}
                {saveMessage && (
                    <div
                        className="mb-16 d-flex align-center gap-8"
                        style={{
                            background: '#f0fdf4',
                            color: '#166534',
                            border: '1px solid #bbf7d0',
                            padding: '10px 14px',
                            borderRadius: '6px',
                            fontSize: '12.5px',
                            fontWeight: 500
                        }}
                    >
                        <FontAwesomeIcon icon={faCheck} className="text-success" />
                        <span>{saveMessage}</span>
                    </div>
                )}

                {/* ================= 2. 4-CARD KPI RIBBON ================= */}
                <div className="dome-kpi-strip">
                    {Object.entries(ROLE_INFO).map(([rId, info]) => {
                        const count = users.filter(u => Number(u.RoleID) === Number(rId)).length;
                        const isFiltered = selectedRoleFilter === rId;

                        return (
                            <div
                                key={rId}
                                className={`dome-kpi-card cursor-pointer ${isFiltered ? 'active-filter-card' : ''}`}
                                onClick={() => {
                                    const next = isFiltered ? 'ALL' : rId;
                                    setSelectedRoleFilter(next);
                                    setActiveMatrixTab(next === 'ALL' ? 'ALL' : Number(next));
                                    setCurrentPage(1);
                                }}
                                title={`Filter view by ${info.label}`}
                            >
                                <div className="d-flex align-center justify-between">
                                    <span className="dome-kpi-label">{info.label} (Role {rId})</span>
                                </div>
                                <div className="dome-kpi-val-row">
                                    <span className="dome-kpi-num">{count}</span>
                                    <span className="dome-kpi-sub">user{count !== 1 ? 's' : ''}</span>
                                </div>
                                <span className="dome-kpi-sub">{info.desc}</span>
                            </div>
                        );
                    })}
                </div>

                {/* ================= 3. USER ROLE ASSIGNMENTS CARD ================= */}
                <div className="dome-dash-card mb-24">
                    <div className="dome-dash-card-header">
                        <div>
                            <h3 className="dome-dash-card-title">
                                User Role Assignments
                                {isSuper && selectedOrg && (
                                    <span className="font-12 text-muted font-normal ml-8">
                                        — {selectedOrg.OrgName}
                                    </span>
                                )}
                            </h3>
                        </div>

                        <div className="dome-table-header-controls">
                            {/* Search Box */}
                            <div className="dome-search-full-box" style={{ width: '260px' }}>
                                <FontAwesomeIcon icon={faSearch} className="dome-search-full-icon" />
                                <input
                                    type="text"
                                    placeholder="Search user, email, designation..."
                                    value={userSearch}
                                    onChange={(e) => {
                                        setUserSearch(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="dome-search-full-input"
                                />
                                {userSearch && (
                                    <button
                                        type="button"
                                        className="dome-search-clear-mini"
                                        onClick={() => {
                                            setUserSearch('');
                                            setCurrentPage(1);
                                        }}
                                        title="Clear search"
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                )}
                            </div>

                            {/* Clear Role Filter if active */}
                            {selectedRoleFilter !== 'ALL' && (
                                <button
                                    type="button"
                                    className="dome-clear-filters-mini"
                                    onClick={() => {
                                        setSelectedRoleFilter('ALL');
                                        setActiveMatrixTab('ALL');
                                        setCurrentPage(1);
                                    }}
                                    title="Show all roles"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="mr-4px" />
                                    Clear Filter
                                </button>
                            )}

                            {/* Entries Selector */}
                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Show:</span>
                                <select
                                    value={entries}
                                    onChange={(e) => {
                                        setEntries(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="dome-mini-select"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="dome-dash-table-wrap">
                        <table className="dome-dash-table">
                            <thead>
                                <tr>
                                    <th className="th-id">ID</th>
                                    <th>User</th>
                                    <th>Email</th>
                                    <th>Designation</th>
                                    <th>Role Assignment</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="td-empty text-center">
                                            <FontAwesomeIcon icon={faSyncAlt} spin className="mr-6px text-primary" />
                                            Loading user accounts and role assignments...
                                        </td>
                                    </tr>
                                ) : paginatedUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="td-empty text-center">
                                            {userSearch || selectedRoleFilter !== 'ALL'
                                                ? "No team members match the search and filter criteria."
                                                : "No users registered in this organization."}
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedUsers.map(u => {
                                        const rinfo = ROLE_INFO[u.RoleID] || { label: 'Unknown' };
                                        const isSelf = u.empId === currentUser?.empId;

                                        return (
                                            <tr key={u.empId}>
                                                {/* ID */}
                                                <td>
                                                    <span className="dome-id-pill">{u.empId}</span>
                                                </td>

                                                {/* User Name */}
                                                <td>
                                                    <div className="dome-lead-name-col">
                                                        <span className="dome-lead-name-link font-600">
                                                            {u.name} {isSelf && <span className="self-tag ml-6">You</span>}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Email */}
                                                <td>{u.email}</td>

                                                {/* Designation */}
                                                <td>{u.designation || '—'}</td>

                                                {/* Role Assignment Select */}
                                                <td>
                                                    <select
                                                        value={u.RoleID}
                                                        onChange={(e) => handleUserRoleChange(u.empId, e.target.value)}
                                                        className="dome-stacked-select"
                                                        disabled={!isSuper && Number(u.RoleID) === ROLES.SUPERADMIN}
                                                    >
                                                        {isSuper && <option value={99}>Superadmin (Role 99)</option>}
                                                        <option value={98}>Org Admin (Role 98)</option>
                                                        <option value={97}>Manager (Role 97)</option>
                                                        <option value={96}>Employee (Role 96)</option>
                                                    </select>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer Pagination */}
                    <div className="dome-table-footer">
                        <span className="dome-table-counter">
                            Showing {filteredUsers.length === 0 ? 0 : (currentPage - 1) * entries + 1}&ndash;{Math.min(currentPage * entries, filteredUsers.length)} of {filteredUsers.length} users
                            {selectedRoleFilter !== 'ALL' && ` (filtered by ${ROLE_INFO[selectedRoleFilter]?.label})`}
                        </span>

                        <div className="dome-pagination-controls">
                            <button
                                type="button"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                                className="dome-page-btn"
                            >
                                Prev
                            </button>

                            <span className="dome-page-indicator">
                                Page {currentPage} of {totalPages || 1}
                            </span>

                            <button
                                type="button"
                                disabled={currentPage === totalPages || totalPages === 0}
                                onClick={() => setCurrentPage(p => p + 1)}
                                className="dome-page-btn"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                {/* ================= 4. PERMISSION MATRIX SECTION ================= */}
                <div className="dome-dash-header-row">
                    <h2 className="dome-dash-h1">CRM Module Permissions Matrix</h2>
                </div>

                {/* Role Filter Tabs */}
                <div className="dome-leads-subnav">
                    <div className="dome-view-toggle-group">
                        <button
                            type="button"
                            className={`dome-view-toggle-btn ${activeMatrixTab === 'ALL' ? 'active' : ''}`}
                            onClick={() => {
                                setActiveMatrixTab('ALL');
                                setSelectedRoleFilter('ALL');
                            }}
                        >
                            <span>All Roles</span>
                        </button>
                        {isSuper && (
                            <button
                                type="button"
                                className={`dome-view-toggle-btn ${activeMatrixTab === 99 ? 'active' : ''}`}
                                onClick={() => {
                                    setActiveMatrixTab(99);
                                    setSelectedRoleFilter('99');
                                }}
                            >
                                <FontAwesomeIcon icon={faShieldAlt} />
                                <span>Superadmin (99)</span>
                            </button>
                        )}
                        <button
                            type="button"
                            className={`dome-view-toggle-btn ${activeMatrixTab === 98 ? 'active' : ''}`}
                            onClick={() => {
                                setActiveMatrixTab(98);
                                setSelectedRoleFilter('98');
                            }}
                        >
                            <FontAwesomeIcon icon={faShieldAlt} />
                            <span>Org Admin (98)</span>
                        </button>
                        <button
                            type="button"
                            className={`dome-view-toggle-btn ${activeMatrixTab === 97 ? 'active' : ''}`}
                            onClick={() => {
                                setActiveMatrixTab(97);
                                setSelectedRoleFilter('97');
                            }}
                        >
                            <FontAwesomeIcon icon={faUsers} />
                            <span>Manager (97)</span>
                        </button>
                        <button
                            type="button"
                            className={`dome-view-toggle-btn ${activeMatrixTab === 96 ? 'active' : ''}`}
                            onClick={() => {
                                setActiveMatrixTab(96);
                                setSelectedRoleFilter('96');
                            }}
                        >
                            <FontAwesomeIcon icon={faUser} />
                            <span>Employee (96)</span>
                        </button>
                    </div>
                </div>


                {matrixSections.map(({ roleId, title, desc, perms, editable }) => (
                    <div key={title} className="dome-dash-card mb-20">
                        <div className="dome-dash-card-header">
                            {/* <div className="dome-dash-left-col"> */}
                            <FontAwesomeIcon icon={ROLE_INFO[roleId]?.icon || faShieldAlt} className="text-primary font-14" />
                            <div>
                                <h3 className="dome-dash-card-title">
                                    {title}
                                    {isSuper && selectedOrg && roleId !== 99 && (
                                        <span className="font-12 text-muted font-normal ml-8">
                                            — {selectedOrg.OrgName}
                                        </span>
                                    )}
                                </h3>
                            </div>
                            {/* </div> */}
                            {!editable ? (
                                <span className="badge-status badge-neutral">
                                    <span className="dome-dot dot-neutral" />
                                    Protected (Superadmin Only)
                                </span>
                            ) : (
                                <span className="badge-status badge-paid">
                                    <span className="dome-dot dot-paid" />
                                    Active Policy
                                </span>
                            )}
                        </div>

                        <div className="dome-dash-table-wrap">
                            <table className="dome-dash-table">
                                <thead>
                                    <tr>
                                        <th style={{ minWidth: '150px' }}>Module</th>
                                        <th className="text-center" style={{ width: '90px' }}>View All</th>
                                        <th className="text-center" style={{ width: '90px' }}>Create</th>
                                        <th className="text-center" style={{ width: '90px' }}>Edit</th>
                                        <th className="text-center" style={{ width: '90px' }}>Delete</th>
                                        <th className="text-center" style={{ width: '120px' }}>Assign Targets</th>
                                        <th>Description</th>
                                        <th className="text-right" style={{ width: '90px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {perms.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="td-empty text-center">
                                                No explicit rules configured for this role.
                                            </td>
                                        </tr>
                                    ) : perms.map(p => (
                                        <tr key={p.PermissionID}>
                                            {/* Module Name */}
                                            <td>
                                                <strong className="text-dark font-12">{p.Module}</strong>
                                            </td>

                                            {/* Permission Checkboxes */}
                                            {['CanViewAll', 'CanCreate', 'CanEdit', 'CanDelete', 'CanAssignTargets'].map(field => (
                                                <td key={field} className="text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(p[field])}
                                                        disabled={!editable}
                                                        onChange={() => handleTogglePermission(p.PermissionID, field)}
                                                        className="cursor-pointer"
                                                        style={{ width: '15px', height: '15px' }}
                                                    />
                                                </td>
                                            ))}

                                            {/* Description */}
                                            <td>
                                                <span className="text-muted font-11">{p.Description || '—'}</span>
                                            </td>

                                            {/* Save Action */}
                                            <td>
                                                <div className="dome-row-actions">
                                                    {editable && (
                                                        <button
                                                            type="button"
                                                            className="btn-dome-secondary"
                                                            style={{ padding: '3px 8px', fontSize: '11px' }}
                                                            onClick={() => handleSavePermission(p)}
                                                            title="Save rule changes"
                                                        >
                                                            <FontAwesomeIcon icon={faSave} className="mr-4px" />
                                                            Save
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="dome-table-footer">
                            <span className="dome-table-counter">
                                {perms.length} functional CRM module policies configured
                            </span>
                            {editable && (
                                <span className="font-11 text-muted">
                                    Toggle checkboxes and click Save to apply changes to this role
                                </span>
                            )}
                        </div>
                    </div>
                ))}

            </div>
        </Layout>
    );
}

export default AccessControl;
